/**
 * Provenance gate for the linked-context ids a chat send may attach.
 *
 * The staging atoms (`linkedSessionContextAtom` / `linkedPatientContextAtom`)
 * are global: `useChatContextLinking`'s backend-sync writes the OPEN chat's
 * link into them (origin = that chat's id) so the composer badge renders.
 * Send paths that read the atoms raw therefore inherit another chat's
 * context — a new chat created from the Work-home `?query=` redirect was
 * born linked to the most recently viewed session's patient without the
 * user staging anything.
 *
 * This helper is the single sanctioned read for attaching those ids to a
 * request. It reads the store FRESH at the send boundary (render closures
 * race the stale-context cleanup effect) and attaches only when the
 * provenance is compatible with the sending chat:
 *
 *   - `'PENDING'`  — staged by the user for a new chat (picker, inbox bar)
 *   - the sending chat's own id — this chat's link (backend-synced or
 *     re-linked mid-chat), legal to re-send for EVI-2279 persistence
 *
 * `'PENDING'` is deliberately CHAT-AGNOSTIC: the created chat's id does not
 * exist at staging time, and `useChatContextLinking`'s first-mount adoption
 * (`null → chatId` while the origin is 'PENDING') cannot distinguish "this
 * chat was just created from the staging" from "the user navigated to an
 * existing chat with staging left behind". A known residual of the same
 * cross-chat class therefore sits OUTSIDE this gate: stage on the new-chat
 * page, navigate to an existing chat without sending, and the pending
 * staging is adopted into that chat. Closing it means making the adoption
 * creation-scoped (e.g. adopt only when the arriving chat id is the
 * client-minted id of the create that consumed the staging) — a
 * `useChatContextLinking` change, not a widening of this gate.
 *
 * Anything else — a DIFFERENT chat's id, or a missing origin — is refused.
 * The gate FAILS CLOSED on a missing origin: staging without provenance can
 * only come from a writer that bypassed the `stageLinkedSessionAtom` /
 * `stageLinkedPatientAtom` setters (or the origin-stamping wrapped setters
 * in `useChatContextLinking`), and attaching unattributed clinical context
 * risks the exact cross-chat PHI bleed this gate exists to close. A dropped
 * attach is recoverable (re-stage, or the remote kill switch below); a
 * wrong-patient attach is not.
 *
 * `options.gateKilled` (`op-evidence-disable-linked-context-provenance-gate`)
 * bypasses the check entirely and restores the pre-gate raw-atom reads —
 * the no-deploy recovery if a missed writer ever leaves legitimate staging
 * refused.
 */

import {
  linkedContextOriginAtom,
  linkedPatientContextAtom,
  linkedSessionContextAtom,
  PENDING_LINKED_CONTEXT_ORIGIN,
} from '@/atoms/chatAtoms';
import type { getDefaultStore } from 'jotai';

type JotaiStore = ReturnType<typeof getDefaultStore>;

export interface AttachableLinkedContext {
  sessionId: string | null;
  patientProfileId: string | null;
  /**
   * True when staging was PRESENT but the gate refused it (foreign or
   * missing origin). False on a pass, on empty staging (nothing to refuse),
   * and under the kill switch (nothing refused). Observability only — folded
   * as a boolean into the send's existing RUM tracking metadata
   * (`linked_context_gated`), so refusals in prod are a dashboard filter
   * rather than an Intercom ticket: it both proves the gate is firing on
   * foreign-origin carryover and surfaces over-refusal (legitimate staging
   * silently not attaching), which is the kill-switch decision input.
   */
  refused: boolean;
}

/**
 * Resolve the linked-context ids the current send may attach.
 *
 * @param store the app Jotai store (read fresh — never pass render values)
 * @param effectiveChatId the chat the send targets; `null`/`undefined` for a
 *   chat that does not exist yet (creation sends)
 * @param options.gateKilled remote kill switch — see module docstring
 */
export function resolveAttachableLinkedContext(
  store: JotaiStore,
  effectiveChatId: string | null | undefined,
  options?: { gateKilled?: boolean }
): AttachableLinkedContext {
  const sessionId = store.get(linkedSessionContextAtom)?.id ?? null;
  const patientProfileId = store.get(linkedPatientContextAtom)?.id ?? null;

  if (!options?.gateKilled) {
    const origin = store.get(linkedContextOriginAtom);
    const attachable =
      origin === PENDING_LINKED_CONTEXT_ORIGIN ||
      (origin !== null && origin === (effectiveChatId ?? null));
    if (!attachable) {
      return {
        sessionId: null,
        patientProfileId: null,
        refused: sessionId !== null || patientProfileId !== null,
      };
    }
  }

  return { sessionId, patientProfileId, refused: false };
}
